import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { SHIFT_STATUS_ONGOING, SHIFT_STATUS_PAUSED, SHIFT_STATUS_VALIDATE } from '../constants.ts';
import { getIntegrations, getShift, getVehicles } from '../queries/getVehicles.ts';
import { Shift } from '../schema/Shift.ts';
import { IVehicle } from '../schema/Vehicle.ts';

export const useShiftByVehicle = (territorySlug: string, vehicleId: number | undefined) => {
  return useQuery({
    queryKey: ['shift', vehicleId],
    queryFn: ({ signal }) => getShift(territorySlug, vehicleId, signal),
    enabled: vehicleId !== undefined,
    throwOnError: true,
  });
};
export const useIntegrationsByVehicle = (territorySlug: string, vehicleId: number | undefined) => {
  return useQuery({
    queryKey: ['integrations', vehicleId],
    queryFn: ({ signal }) => getIntegrations(territorySlug, vehicleId, signal),
    enabled: vehicleId !== undefined,
    throwOnError: true,
  });
};

export const useVehiclesShiftByTerritory = (territorySlug: string) => {
  return useQuery({
    queryKey: ['vehicles', territorySlug],
    queryFn: async ({ signal }) => {
      const vehiclesRes = await getVehicles(territorySlug, signal);
      const shifts = await Promise.all(
        vehiclesRes.map((v: IVehicle) =>
          getShift(territorySlug, v.id, signal).then((s: Shift | null) => ({
            vehicleId: v.id,
            shift: s,
          })),
        ),
      );
      const vehiclesWithShifts = vehiclesRes.map((v: IVehicle) => {
        const s = shifts.find((s: { vehicleId: number; shift: Shift | null }) => s.vehicleId === v.id);
        return { ...v, shift: s?.shift };
      });

      return { ...vehiclesRes, data: vehiclesWithShifts };
    },
    refetchInterval: query => (query.state.error ? false : 15000),
  });
};

export const useGroupedVehiclesByTerritory = (territorySlug: string) => {
  const { data: vehiclesData, ...rest } = useVehiclesShiftByTerritory(territorySlug);

  const grouped = useMemo(() => {
    const vehicles: IVehicle[] = vehiclesData?.data ?? [];

    const InOperation: IVehicle[] = [];
    const PlannedOperation: IVehicle[] = [];
    const Inactive: IVehicle[] = [];

    for (const vehicle of vehicles) {
      if (vehicle?.shift && [SHIFT_STATUS_ONGOING].includes(vehicle.shift.status)) {
        InOperation.push(vehicle);
      } else if (
        vehicle?.shift &&
        [SHIFT_STATUS_VALIDATE, SHIFT_STATUS_PAUSED].includes(vehicle.shift.status)
      ) {
        PlannedOperation.push(vehicle);
      } else {
        Inactive.push(vehicle);
      }
    }

    return {
      InOperation,
      PlannedOperation,
      Inactive,
      total: vehicles.length,
    };
  }, [vehiclesData?.data]);
  // console.log(...rest);
  return {
    ...rest,
    grouped,
  };
};
